import { Box, Divider } from "@mui/material";
import type { Dispatch, FC, SetStateAction } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useJobColors } from "../../../../hooks/useJobColors";
import { CONTROL_HEIGHT, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { type Job, type NurseRole, ShiftType } from "../../../../utils/models";
import { DayViewLocationFilter } from "../../../DayViewContent/DayViewLocationFitler";
import { RoleOrJobSelector } from "../../../UIToolkit/molecules/RoleOrJobSelector";
import { SwatchLegend, type SwatchLegendItem } from "../../../UIToolkit/molecules/SwatchLegend";
import { TimeFilterChips } from "../../../UIToolkit/molecules/TimeFilterChips";
import { type ExactShiftFilterType, ShiftFilterType } from "../utils";
import { formatShiftHour, MONTHLY_OTHER_WINDOW_KEY, type MonthlyShiftWindow, roundedWindowKey, StaffingTotalsMode } from "./monthlyJobUtils";
import { getWindowPresentation } from "./monthlyWindowPresentation";

type Props = {
	/** The department's derived shift-time windows — the chips offer exactly these, in start order. */
	windows: readonly MonthlyShiftWindow[];
	windowKey: string | null;
	onWindowKeyChange: (windowKey: string | null) => void;
	roleFilter: (NurseRole | "all")[];
	onRoleFilterChange: (roles: (NurseRole | "all")[]) => void;
	jobFilterIds: string[];
	onJobFilterChange: (jobIds: string[]) => void;
	visibleJobs: Job[];
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
	totalsMode: StaffingTotalsMode;
};

/**
 * The Monthly view's filter row, in the Day view's order: locations → shift-time windows → divider →
 * role/job. It is deliberately ONE line with a bounded height — six grid rows divide whatever height is
 * left over, so every wrapped filter line comes straight out of the cells.
 *
 * The legend lives here rather than inside 42 cells: the grid encodes window as position + band + glyph
 * and job as hue, and this says so exactly once.
 */
export const MonthlyFilterBar: FC<Props> = ({
	windows,
	windowKey,
	onWindowKeyChange,
	roleFilter,
	onRoleFilterChange,
	jobFilterIds,
	onJobFilterChange,
	visibleJobs,
	selectedLocations,
	setSelectedLocations,
	availableLocations,
	totalsMode,
}) => {
	const { t } = useTranslation();
	const jobColor = useJobColors();
	const hasOtherBucket = windows.some((window) => window.key === MONTHLY_OTHER_WINDOW_KEY);

	// The `Other` bucket carries no range, so it becomes a `ShiftFilterType.Other` chip rather than a time.
	const timeWindows = useMemo<ExactShiftFilterType[]>(
		() => windows.flatMap((window) => (window.start !== null && window.end !== null ? [{ start: window.start, end: window.end }] : [])),
		[windows],
	);

	const selectedFilters = useMemo<(ExactShiftFilterType | ShiftFilterType)[]>(() => {
		if (windowKey === null) return [ShiftFilterType.All];
		if (windowKey === MONTHLY_OTHER_WINDOW_KEY) return [ShiftFilterType.Other];
		const selected = timeWindows.find((time) => roundedWindowKey(time.start, time.end) === windowKey);
		return selected ? [selected] : [ShiftFilterType.All];
	}, [windowKey, timeWindows]);

	const handleFilterChange = useCallback(
		(filter: ExactShiftFilterType | ShiftFilterType) => {
			if (filter === ShiftFilterType.All) return onWindowKeyChange(null);
			if (filter === ShiftFilterType.Other) return onWindowKeyChange(MONTHLY_OTHER_WINDOW_KEY);
			if (typeof filter === "string") return onWindowKeyChange(null);
			return onWindowKeyChange(roundedWindowKey(filter.start, filter.end));
		},
		[onWindowKeyChange],
	);

	// Both dimensions the cells encode, keyed once. Derived from the SAME presentation helper the cells
	// render from, so the legend can never describe a color the grid isn't using.
	const legendItems = useMemo<SwatchLegendItem[]>(() => {
		const items: SwatchLegendItem[] = [];
		if (totalsMode !== StaffingTotalsMode.ByJob) {
			for (const band of [ShiftType.Day, ShiftType.Night]) {
				const window = windows.find((candidate) => candidate.band === band);
				if (!window) continue;
				const { tint, rail, glyph } = getWindowPresentation(window);
				items.push({
					id: `band-${band}`,
					label: t(band === ShiftType.Night ? "monthly.view.legend.night.windows" : "monthly.view.legend.day.windows"),
					fill: tint,
					rail,
					glyph,
				});
			}
		}
		if (totalsMode !== StaffingTotalsMode.ByShift) {
			for (const job of visibleJobs) {
				items.push({ id: `job-${job.id}`, label: job.displayName, fill: jobColor(job.id).dot, round: true });
			}
		}
		return items;
	}, [totalsMode, windows, visibleJobs, t, jobColor]);

	return (
		<Box
			data-testid="monthly-view-filter-bar"
			sx={{
				display: "flex",
				alignItems: "center",
				flexWrap: "nowrap",
				gap: 1.5,
				px: 2,
				py: 0.5,
				minHeight: CONTROL_HEIGHT.STANDARD,
				borderBottom: `1px solid ${NEUTRAL[200]}`,
				overflow: "hidden",
			}}
		>
			<Box data-testid="monthly-view-location-filter" sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
				<DayViewLocationFilter selectedLocations={selectedLocations} setSelectedLocations={setSelectedLocations} availableLocations={availableLocations} />
			</Box>

			{/* One line, scrolled rather than wrapped — the cap (`MONTHLY_MAX_SHIFT_WINDOWS` + All + Other)
			    means this only ever engages on a very narrow viewport. */}
			<Box sx={{ minWidth: 0, overflowX: "auto", overflowY: "hidden", py: 0.25 }}>
				<TimeFilterChips
					allShiftsTimes={[]}
					exactTimes={timeWindows}
					singleSelect
					allowFilterOtherShifts={hasOtherBucket}
					formatLabel={(time) => `${formatShiftHour(time.start)}–${formatShiftHour(time.end)}`}
					selectedFilters={selectedFilters}
					onSelectedFilterChange={handleFilterChange}
					rootSx={{ flexWrap: "nowrap" }}
					dataTestId="monthly-view-shift-filter"
				/>
			</Box>

			<Divider orientation="vertical" flexItem sx={{ borderColor: NEUTRAL[300], flexShrink: 0 }} />

			<Box data-testid="monthly-view-job-filter" sx={{ flexShrink: 0 }}>
				<RoleOrJobSelector
					selectedRoles={roleFilter}
					onSelectedRolesChanged={onRoleFilterChange}
					selectedJobIds={jobFilterIds}
					onSelectedJobsChanged={onJobFilterChange}
					availableJobIds={visibleJobs.map((job) => job.id)}
					label={t("job")}
					formControlSx={{ minWidth: "220px" }}
					selectSx={{ background: SURFACE }}
				/>
			</Box>

			{/* Hidden below `lg`, where the row has no spare width: every window is still named on its own
			    cell row and in the drawer, so nothing is lost — only restated. */}
			{legendItems.length > 0 && (
				<SwatchLegend
					dense
					title={t("monthly.view.legend.title")}
					items={legendItems}
					dataTestId="monthly-view-legend"
					sx={{ ml: "auto", flexShrink: 1, overflow: "hidden", display: { xs: "none", lg: "flex" } }}
				/>
			)}
		</Box>
	);
};
